import React, { useState } from "react";
import { CheckIcon, TrashIcon, RecurringIcon, EditIcon, ClockIcon } from "./Icons";
import "./TodoItem.css";

function formatCreatedDate(dateStr) {
  const d = new Date(dateStr);
  const diff = Date.now() - d;
  if (diff < 60000) return "Just now";
  if (diff < 3600000) return Math.floor(diff / 60000) + "m ago";
  if (diff < 86400000) return Math.floor(diff / 3600000) + "h ago";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatDueDate(dateStr) {
  // due_date is YYYY-MM-DD — parse as local date to avoid timezone shift
  const clean = typeof dateStr === "string" ? dateStr.split("T")[0] : "";
  if (!clean) return { label: "Due soon" };
  const [y, m, d] = clean.split("-").map(Number);
  const due = new Date(y, m - 1, d);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = due - today;
  if (diff < 0) return { label: "Overdue", overdue: true };
  if (diff === 0) return { label: "Due today", today: true };
  if (diff === 86400000) return { label: "Due tomorrow", soon: true };
  return { label: "Due " + due.toLocaleDateString("en-US", { month: "short", day: "numeric" }) };
}

function getTimeRemaining(dateStr, timeStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [h, min] = timeStr.split(":").map(Number);
  const due = new Date(y, m - 1, d, h, min);
  const diff = due - Date.now();
  const overdue = diff < 0;
  const abs = Math.abs(diff);
  const days = Math.floor(abs / 86400000);
  const hours = Math.floor((abs % 86400000) / 3600000);
  const mins = Math.floor((abs % 3600000) / 60000);
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0 || parts.length === 0) parts.push(`${mins}m`);
  return { label: parts.join(" "), overdue };
}

export default function TodoItem({ todo, onToggle, onDelete, onEdit, categories }) {
  const [editing, setEditing] = useState(false);
  const [showTimeLeft, setShowTimeLeft] = useState(false);
  const [title, setTitle] = useState(todo.title);
  const [description, setDescription] = useState(todo.description || "");
  const [priority, setPriority] = useState(todo.priority);
  const [categoryId, setCategoryId] = useState(todo.category_id ? String(todo.category_id) : "");
  const [dueDate, setDueDate] = useState(todo.due_date ? todo.due_date.split("T")[0] : "");
  const [dueTime, setDueTime] = useState(todo.due_time ? todo.due_time.slice(0, 5) : "");

  const handleSave = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    await onEdit(todo.id, {
      title: title.trim(),
      description: description.trim(),
      priority,
      category_id: categoryId ? parseInt(categoryId) : null,
      due_date: dueDate || null,
      due_time: dueTime || null,
    });
    setEditing(false);
  };

  const handleCancel = () => {
    setTitle(todo.title);
    setDescription(todo.description || "");
    setPriority(todo.priority);
    setCategoryId(todo.category_id ? String(todo.category_id) : "");
    setDueDate(todo.due_date ? todo.due_date.split("T")[0] : "");
    setDueTime(todo.due_time ? todo.due_time.slice(0, 5) : "");
    setEditing(false);
  };

  const due = todo.due_date && !todo.recurrence_id ? formatDueDate(todo.due_date) : null;
  const timeLeft = showTimeLeft && todo.due_date && todo.due_time
    ? getTimeRemaining(todo.due_date.split("T")[0], todo.due_time.slice(0, 5))
    : null;

  if (editing) {
    return (
      <div className="todo-item todo-item-editing">
        <form className="edit-form" onSubmit={handleSave}>
          <input
            className="edit-title"
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            autoFocus
          />
          <input
            className="edit-desc"
            type="text"
            placeholder="Description (optional)"
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
          <div className="edit-row">
            <select className="form-select" value={priority} onChange={e => setPriority(e.target.value)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
            <input
              type="date"
              className="form-select"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              title="Due date (optional)"
            />
            <input
              type="time"
              className="form-select"
              value={dueTime}
              onChange={e => setDueTime(e.target.value)}
              title="Due time (optional)"
              disabled={!dueDate}
            />
            <select className="form-select" value={categoryId} onChange={e => setCategoryId(e.target.value)}>
              <option value="">No category</option>
              {(categories || []).filter(c => !c.parent_id).map(parent => {
                const children = (categories || []).filter(c => c.parent_id === parent.id);
                if (children.length === 0) {
                  return <option key={parent.id} value={parent.id}>{parent.name}</option>;
                }
                return (
                  <optgroup key={parent.id} label={parent.name}>
                    <option value={parent.id}>{parent.name} (all)</option>
                    {children.map(c => (
                      <option key={c.id} value={c.id}>— {c.name}</option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          </div>
          <div className="edit-actions">
            <button type="button" className="btn-cancel-edit" onClick={handleCancel}>Cancel</button>
            <button type="submit" className="btn-save-edit">Save</button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className={"todo-item" + (todo.completed ? " completed" : "")}>
      <label className="todo-check">
        <input type="checkbox" checked={todo.completed} onChange={() => onToggle(todo)}/>
        <span className="checkmark"><CheckIcon/></span>
      </label>
      <div className="todo-content">
        <div className="todo-title">{todo.title}</div>
        {todo.description && <div className="todo-desc">{todo.description}</div>}
        <div className="todo-meta">
          <span className={"priority-badge " + todo.priority}>{todo.priority}</span>
          {todo.category_name && (
            <span
              className="cat-badge"
              style={{ borderColor: todo.category_color + "60", color: todo.category_color }}
            >
              {todo.category_name}
            </span>
          )}
          {todo.recurrence_id && (
            <span className="recurring-badge" title="Recurring task">
              <RecurringIcon/> recurring
            </span>
          )}
          {due && (
            <span className={"due-badge" + (due.overdue ? " overdue" : due.today ? " today" : due.soon ? " soon" : "")}>
              {due.label}
            </span>
          )}
          {timeLeft && (
            <span className={"time-left-badge" + (timeLeft.overdue ? " overdue" : "")}>
              {timeLeft.overdue ? `${timeLeft.label} overdue` : `${timeLeft.label} left`}
            </span>
          )}
          <span className="todo-date">{formatCreatedDate(todo.created_at)}</span>
        </div>
      </div>
      {todo.due_time && (
        <button
          className={"btn-icon clock" + (showTimeLeft ? " active" : "")}
          onClick={() => setShowTimeLeft(v => !v)}
          title="Show time remaining"
        >
          <ClockIcon/>
        </button>
      )}
      <button className="btn-icon edit" onClick={() => setEditing(true)} title="Edit">
        <EditIcon/>
      </button>
      <button className="btn-icon delete" onClick={() => onDelete(todo.id)} title="Delete">
        <TrashIcon/>
      </button>
    </div>
  );
}
